import { ReactElement, useCallback } from 'react'
import Accordion from 'ui/molecules/Accordion'
import { SectionID } from 'ui/molecules/FrequentlyAskedQuestions/typings'
import { AnyObject, I18N, Translation } from 'typings/custom'
import { Stack } from 'syf-component-library/ui/spacing'
import {
  AddressWrapper,
  List,
  Question,
  QuestionSection,
  Text
} from 'ui/molecules/FrequentlyAskedQuestions/subcomponents'
import { cities, stateList } from 'const/states'
import { Address } from 'typings/address'
import clientInfo from '../clientInfo'
import { translations } from '../brandTranslations'

interface FAQQuestionList {
  id: string
  question: () => ReactElement
}

interface SectionProps {
  i18n: I18N
  hasPaymentAccess: boolean
  hasCreditAccess: boolean
}

interface SectionConfig {
  sectionId: SectionID
  title: string
  exclude?: boolean
}

type FaqQuestion = {
  [key in SectionID]?: Array<FAQQuestionList>
}

const {
  customerSupport: { contactUsBrcPlcc }
} = clientInfo

const addressee = 'Addressee'
const addressLine1 = 'Address Line 1'
const addressLine2 = 'Address Line 2'

// addresses specific to p66 plcc
const brcDirectPayment: Address = {
  addressee: 'Phillips 66 BRC',
  addressLine1: 'PO Box 669824',
  city: cities.dallas,
  state: stateList.texas,
  zipCode: '75266-0957'
}

const brcPaymentResearch: Address = {
  addressee: 'Payments Research',
  addressLine1: 'PO BOX  71728',
  city: cities.philadelphia,
  state: stateList.pennsylvania,
  zipCode: '19176-1728'
}

// we have two different brcDispute Addresses
const brcDispute: Address = {
  addressee: 'Phillips 66 Consumer/PLCC & BRC',
  addressLine1: 'PO Box 71726',
  city: cities.philadelphia,
  state: stateList.pennsylvania,
  zipCode: '19176- 1726'
}

const brcDispute2: Address = {
  addressee: 'Phillips 66 Consumer/PLCC & BRC',
  addressLine1: 'PO Box 71727',
  city: cities.philadelphia,
  state: stateList.pennsylvania,
  zipCode: '19176- 1727'
}

// get the faq modules
const getSections = (
  translate: (module: string, key: string) => Translation,
  {
    hasPaymentAccess,
    hasCreditAccess
  }: {
    hasPaymentAccess: boolean
    hasCreditAccess: boolean
  }
): Array<SectionConfig> => {
  const modules: Array<SectionConfig> = [
    {
      sectionId: 'login',
      title: translate('frequentlyAskedQuestions', 'Login Questions') as string
    },
    {
      sectionId: 'payment',
      title: translate('frequentlyAskedQuestions', 'Payments') as string,
      exclude: !hasPaymentAccess
    },
    {
      sectionId: 'technical',
      title: translate('frequentlyAskedQuestions', 'Technical Help') as string
    },
    {
      sectionId: 'interestcharge',
      title: translate(
        'frequentlyAskedQuestions',
        'Interest Charges and Late Fees'
      ) as string
    },
    {
      sectionId: 'webpageissue',
      title: translate('frequentlyAskedQuestions', 'Web Page Issues') as string
    },
    {
      sectionId: 'closeaccount',
      title: translate('frequentlyAskedQuestions', 'Close Account') as string
    },
    {
      sectionId: 'contactus',
      title: translate('frequentlyAskedQuestions', 'Contact Us') as string
    },
    {
      sectionId: 'updateaccountinfo',
      title: translate(
        'frequentlyAskedQuestions',
        'Update account Information'
      ) as string
    },
    {
      sectionId: 'accountactivity',
      title: translate('frequentlyAskedQuestions', 'Account Activity') as string
    },
    {
      sectionId: 'replacementcard',
      title: translate(
        'frequentlyAskedQuestions',
        'Replacement Cards'
      ) as string
    },
    {
      sectionId: 'securityinformation',
      title: translate(
        'frequentlyAskedQuestions',
        'Security Information'
      ) as string
    },
    {
      sectionId: 'credit',
      title: translate('frequentlyAskedQuestions', 'Credit') as string,
      exclude: !hasCreditAccess
    },
    {
      sectionId: 'onlinemessage',
      title: translate(
        'frequentlyAskedQuestions',
        'Online Message Center'
      ) as string
    }
  ]

  /*
   *return the valid modules
   *some modules need to have invoice, payment or credit access
   */
  return modules.filter(section => !section.exclude)
}

// get the faq questions
const getFaqQuestions = (
  translate: (module: string, key: string, data?: AnyObject) => Translation
) => {
  const FaqQuestions: FaqQuestion = {
    login: [
      {
        id: 'personalInfoQuestion',
        question: (): ReactElement => (
          <QuestionSection>
            <Question>
              {translate(
                'faqLoginQuestions',
                'Why do I need to enter personal information?'
              )}
            </Question>
            <Text>
              {translate(
                'faqLoginAnswers',
                'We require you to provide us personal information...'
              )}
            </Text>
          </QuestionSection>
        )
      },
      {
        id: 'informationSecure',
        question: (): ReactElement => (
          <QuestionSection>
            <Question>
              {translate(
                'faqLoginQuestions',
                'Is the information that I enter secure?'
              )}
            </Question>
            <Text>
              {translate(
                'faqLoginAnswers',
                'Yes, we use SSL encryption, an industry standard, to help...'
              )}
            </Text>
          </QuestionSection>
        )
      }
    ],
    payment: [
      {
        id: 'paymentposted',
        question: (): ReactElement => (
          <QuestionSection>
            <Question>
              {translate(
                'faqPaymentQuestions',
                'What should I do if a payment I sent has not been posted?'
              )}
            </Question>
            <Text>
              {translate(
                'faqPaymentAnswers',
                'If your payment has not been posted...',
                { customerCare: contactUsBrcPlcc }
              )}
            </Text>
          </QuestionSection>
        )
      },
      {
        id: 'paymentmethod',
        question: (): ReactElement => (
          <QuestionSection>
            <Question>
              {translate(
                'faqPaymentQuestions',
                'What methods of payment can I use to pay my bill?'
              )}
            </Question>
            <Text>
              {translate(
                'faqPaymentAnswers',
                'You can make a One-Time Payment or schedule AutoPay...'
              )}
            </Text>
          </QuestionSection>
        )
      },
      {
        id: 'sendPayments',
        question: (): ReactElement => (
          <QuestionSection>
            <Question>
              {translate('faqPaymentQuestions', 'Where do I send my payments?')}
            </Question>
            <Text>
              {translate(
                'faqPaymentAnswers',
                'You can send your payments to the following address:'
              )}
            </Text>
            <AddressWrapper>
              <Text>
                {translate('common', addressee, {
                  addressee: brcDirectPayment.addressee
                })}
              </Text>
              <Text>
                {translate('common', addressLine1, {
                  addressLine1: brcDirectPayment.addressLine1
                })}
              </Text>
              <Text>
                {translate('common', addressLine2, {
                  city: brcDirectPayment.city,
                  state: brcDirectPayment.state,
                  zipCode: brcDirectPayment.zipCode
                })}
              </Text>
            </AddressWrapper>
            <Stack size="large" />
            <Text>
              {translate('faqPaymentAnswers', 'Make checks payable to:')}
            </Text>
            <Text>{translate('faqPaymentAnswers', 'Phillips 66/SYNCB')}</Text>
          </QuestionSection>
        )
      },
      {
        id: 'paymentrestriction',
        question: (): ReactElement => (
          <QuestionSection>
            <Question>
              {translate(
                'faqPaymentQuestions',
                'Can I make a payment at a Phillips 66 location?'
              )}
            </Question>
            <Text>
              {translate(
                'faqPaymentAnswers',
                'No, you can make a One-Time Payment or...'
              )}
            </Text>
          </QuestionSection>
        )
      },
      {
        id: 'duedatechange',
        question: (): ReactElement => (
          <QuestionSection>
            <Question>
              {translate(
                'faqPaymentQuestions',
                'Can I get my payment due date changed?'
              )}
            </Question>
            <Text>
              {translate(
                'faqPaymentAnswers',
                'Yes, please contact customer service at...',
                { customerCare: contactUsBrcPlcc }
              )}
            </Text>
          </QuestionSection>
        )
      },
      {
        id: 'paymenterror',
        question: (): ReactElement => (
          <QuestionSection>
            <Question>
              {translate(
                'faqPaymentQuestions',
                'The payment posted on my account was for an incorrect amount?'
              )}
            </Question>
            <Text>
              {translate(
                'faqPaymentAnswers',
                'If your statement shows that your payment was received...',
                { customerCare: contactUsBrcPlcc }
              )}
            </Text>
            <Stack size="small" />
            <AddressWrapper>
              <Text>{translate('faqPaymentAnswers', 'Payments Research')}</Text>
              <Stack size="small" />
              <Text>
                {translate('common', addressLine1, {
                  addressLine1: brcPaymentResearch.addressLine1
                })}
              </Text>
              <Stack size="small" />
              <Text>
                {translate('common', addressLine2, {
                  city: brcPaymentResearch.city,
                  state: brcPaymentResearch.state,
                  zipCode: brcPaymentResearch.zipCode
                })}
              </Text>
            </AddressWrapper>
            <Stack size="large" />
            <Text>
              {translate(
                'faqPaymentAnswers',
                'Please follow the instructions below:'
              )}
            </Text>
            <List type="disc">
              <li>
                {translate(
                  'faqPaymentAnswers',
                  'If the amount posted to your account was LESS...'
                )}
              </li>
              <li>
                {translate(
                  'faqPaymentAnswers',
                  'If the amount posted to your account was MORE...'
                )}
              </li>
            </List>
          </QuestionSection>
        )
      },
      {
        id: 'autopay',
        question: (): ReactElement => (
          <QuestionSection>
            <Question>
              {translate('faqPaymentQuestions', 'What is AutoPay?')}
            </Question>
            <Text>
              {translate(
                'faqPaymentAnswers',
                'AutoPay withdraws the payment amount you...'
              )}
            </Text>
          </QuestionSection>
        )
      }
    ],
    technical: [
      {
        id: 'browsercompatibility',
        question: (): ReactElement => (
          <QuestionSection>
            <Question>
              {translate(
                'faqTechnicalHelpQuestions',
                'Browser Requirements and Compatibility'
              )}
            </Question>
            <Text>
              {translate(
                'faqTechnicalHelpAnswers',
                'To login to the Online Credit Center you need a browser which is SSL...'
              )}
            </Text>
          </QuestionSection>
        )
      },
      {
        id: 'onlineserviceunavailability',
        question: (): ReactElement => (
          <QuestionSection>
            <Question>
              {translate(
                'faqTechnicalHelpQuestions',
                'Are there any scheduled times when online customer service is unavailable?'
              )}
            </Question>
            <Text>
              {translate(
                'faqTechnicalHelpAnswers',
                'The Online Credit Center may be down for maintenance...'
              )}
            </Text>
          </QuestionSection>
        )
      }
    ],
    interestcharge: [
      {
        id: 'avoidinterestcharge',
        question: (): ReactElement => (
          <QuestionSection>
            <Question>
              {translate(
                'faqInterestQuestions',
                'How can I avoid interest charges?'
              )}
            </Question>
            <Text>
              {translate(
                'faqInterestAnswers',
                'If there is no previous balance for the billing period...'
              )}
            </Text>
          </QuestionSection>
        )
      },
      {
        id: 'avoidlatepaymentfee',
        question: (): ReactElement => (
          <QuestionSection>
            <Question>
              {translate(
                'faqInterestQuestions',
                'How can I avoid late payment fees?'
              )}
            </Question>
            <Text>
              {translate(
                'faqInterestAnswers',
                'You will not be charged a late payment fee if we receive...'
              )}
            </Text>
          </QuestionSection>
        )
      }
    ],
    webpageissue: [
      {
        id: 'troubleviewingaccount',
        question: (): ReactElement => (
          <QuestionSection>
            <Question>
              {translate(
                'faqWebPageIssueQuestions',
                'What should I do when I am having trouble viewing my account information on the screen?'
              )}
            </Question>
            <Text>
              {translate(
                'faqWebPageIssueAnswers',
                'Please try one or more of the following to resolve the issue:'
              )}
            </Text>
            <Stack size="small" />
            <List type="disc">
              <li>
                {translate(
                  'faqWebPageIssueAnswers',
                  'Press F5 to refresh your browser'
                )}
              </li>
              <li>
                {translate(
                  'faqWebPageIssueAnswers',
                  'Check that the browser you are using is compatible (see Technical Help)'
                )}
              </li>
              <li>
                {translate(
                  'faqWebPageIssueAnswers',
                  'Ensure that pop-up blocker is disabled while on this web site'
                )}
              </li>
              <li>
                {translate(
                  'faqWebPageIssueAnswers',
                  'Check your internet connection to ensure it is functioning properly'
                )}
              </li>
              <li>
                {translate(
                  'faqWebPageIssueAnswers',
                  'If you are still encountering problems after trying the above actions...',
                  { customerCare: contactUsBrcPlcc }
                )}
              </li>
            </List>
          </QuestionSection>
        )
      }
    ],
    closeaccount: [
      {
        id: 'choosecloseaccount',
        question: (): ReactElement => (
          <QuestionSection>
            <Question>
              {translate(
                'faqCloseAccountQuestions',
                'What happens if I choose to close my account?'
              )}
            </Question>
            <Text>
              {translate(
                'faqCloseAccountAnswers',
                'You are a valued customer and we hope you...'
              )}
            </Text>
          </QuestionSection>
        )
      }
    ],
    contactus: [
      {
        id: 'accountinformation',
        question: (): ReactElement => (
          <QuestionSection>
            <Question>
              {translate(
                'faqContactUsQuestions',
                'Customer Service/Account Information'
              )}
            </Question>
            <Text>
              {translate(
                'faqContactUsAnswers',
                'For questions regarding Customer Service or Account Information...',
                { customerCare: contactUsBrcPlcc }
              )}
            </Text>
            <Stack size="small" />
            <Text>
              {translate(
                'faqContactUsAnswers',
                'We are available Monday-Friday 8AM - 1AM EST and...'
              )}
            </Text>
          </QuestionSection>
        )
      }
    ],
    updateaccountinfo: [
      {
        id: 'updatemailphone',
        question: (): ReactElement => (
          <QuestionSection>
            <Question>
              {translate(
                'faqUpdateAccountInformationQuestions',
                'How do I update my mailing address and/or phone number?'
              )}
            </Question>
            <Text>
              {translate(
                'faqUpdateAccountInformationAnswers',
                "You can update your company's mailing address..."
              )}
            </Text>
            <Stack size="large" />
            <Text>
              {translate(
                'faqUpdateAccountInformationAnswers',
                'Legal Name Change'
              )}
            </Text>
            <Stack size="small" />
            <Text>
              {translate(
                'faqUpdateAccountInformationAnswers',
                "To change the company's legal name..."
              )}
            </Text>
            <Stack size="small" />
            <Text>
              {translate(
                'faqUpdateAccountInformationAnswers',
                'Please mail a copy of the legal documents...'
              )}
            </Text>
            <Stack size="small" />
            <AddressWrapper>
              <Text>
                {translate('common', addressee, {
                  addressee: brcDispute.addressee
                })}
              </Text>
              <Text>
                {translate(
                  'faqUpdateAccountInformationAnswers',
                  'General Inquiry Name Change'
                )}
              </Text>
              <Text>
                {translate('common', addressLine1, {
                  addressLine1: brcDispute.addressLine1
                })}
              </Text>
              <Text>
                {translate('common', addressLine2, {
                  city: brcDispute.city,
                  state: brcDispute.state,
                  zipCode: brcDispute.zipCode
                })}
              </Text>
            </AddressWrapper>
          </QuestionSection>
        )
      }
    ],
    accountactivity: [
      {
        id: 'purchaseproof',
        question: (): ReactElement => (
          <QuestionSection>
            <Question>
              {translate(
                'faqAccountActivityQuestions',
                'How do I obtain proof of purchase documentation?'
              )}
            </Question>
            <Text>
              {translate(
                'faqAccountActivityAnswers',
                'Under the “Billing” section select...'
              )}
            </Text>
          </QuestionSection>
        )
      },
      {
        id: 'printaccountactivity',
        question: (): ReactElement => (
          <QuestionSection>
            <Question>
              {translate(
                'faqAccountActivityQuestions',
                'How do I view and print my past account activity?'
              )}
            </Question>
            <Text>
              {translate(
                'faqAccountActivityAnswers',
                'Under the “Billing” section select...'
              )}
            </Text>
          </QuestionSection>
        )
      },
      {
        id: 'downloadinvoices',
        question: (): ReactElement => (
          <QuestionSection>
            <Question>
              {translate(
                'faqAccountActivityQuestions',
                'How can I download Statements?'
              )}
            </Question>
            <List type="disc">
              <li>
                {translate(
                  'faqAccountActivityAnswers',
                  'Select “Billing” at the top of the page'
                )}
              </li>
              <li>
                {translate(
                  'faqAccountActivityAnswers',
                  'Select “eStatements”  and choose the statement you wish to view/download'
                )}
              </li>
              <li>
                {translate(
                  'faqAccountActivityAnswers',
                  'Select the desired “Statement Date”'
                )}
              </li>
              <li>
                {translate(
                  'faqAccountActivityAnswers',
                  'Select the “PDF” button'
                )}
              </li>
            </List>
          </QuestionSection>
        )
      }
    ],
    replacementcard: [
      {
        id: 'lostorstolen',
        question: (): ReactElement => (
          <QuestionSection>
            <Question>
              {translate(
                'faqReplacementCardsQuestions',
                'What should I do if my credit card has been lost or stolen?'
              )}
            </Question>
            <Text>
              {translate(
                'faqReplacementCardsAnswers',
                'You can report your card lost, misplaced...',
                {
                  customerCare: contactUsBrcPlcc
                }
              )}
            </Text>
          </QuestionSection>
        )
      },
      {
        id: 'carddamagedormisplaced',
        question: (): ReactElement => (
          <QuestionSection>
            <Question>
              {translate(
                'faqReplacementCardsQuestions',
                'What should I do if my credit card has been damaged?'
              )}
            </Question>
            <Text>
              {translate(
                'faqReplacementCardsAnswers',
                'You can request a replacement card...',
                {
                  customerCare: contactUsBrcPlcc
                }
              )}
            </Text>
          </QuestionSection>
        )
      },
      {
        id: 'replacementcardnotreceived',
        question: (): ReactElement => (
          <QuestionSection>
            <Question>
              {translate(
                'faqReplacementCardsQuestions',
                'What should I do if I have not received my replacement card?'
              )}
            </Question>
            <Text>
              {translate(
                'faqReplacementCardsAnswers',
                'Contact Customer Service at...',
                {
                  customerCare: contactUsBrcPlcc
                }
              )}
            </Text>
          </QuestionSection>
        )
      }
    ],
    securityinformation: [
      {
        id: 'securesite',
        question: (): ReactElement => (
          <QuestionSection>
            <Question>
              {translate(
                'faqSecurityInformationQuestions',
                'Am I on a secure site even if the padlock on my browser is not locked?'
              )}
            </Question>
            <Text>
              {translate(
                'faqSecurityInformationAnswers',
                'Your account information, credit application and other sensitive...'
              )}
            </Text>
            <Text>
              {translate(
                'faqSecurityInformationAnswers',
                'The reason the padlock icon provided on your browser may...'
              )}
            </Text>
          </QuestionSection>
        )
      },
      {
        id: 'ssl',
        question: (): ReactElement => (
          <QuestionSection>
            <Question>
              {translate('faqSecurityInformationQuestions', 'What is SSL?')}
            </Question>
            <Text>
              {translate(
                'faqSecurityInformationAnswers',
                'SSL is an abbreviation for Secured Socket...'
              )}
            </Text>
          </QuestionSection>
        )
      },
      {
        id: 'securesession',
        question: (): ReactElement => (
          <QuestionSection>
            <Question>
              {translate(
                'faqSecurityInformationQuestions',
                'What is a "secure session" and how does it help keep my information safe?'
              )}
            </Question>
            <Text>
              {translate(
                'faqSecurityInformationAnswers',
                'During a secure SSL (Secured Socket Layer) session...'
              )}
            </Text>
          </QuestionSection>
        )
      },
      {
        id: 'finishedviewaccount',
        question: (): ReactElement => (
          <QuestionSection>
            <Question>
              {translate(
                'faqSecurityInformationQuestions',
                'What should I do when I have finished viewing my account information?'
              )}
            </Question>
            <Text>
              {translate(
                'faqSecurityInformationAnswers',
                'When you have finished using a secure area of Online Credit Center...'
              )}
            </Text>
          </QuestionSection>
        )
      },
      {
        id: 'publiccomputer',
        question: (): ReactElement => (
          <QuestionSection>
            <Question>
              {translate(
                'faqSecurityInformationQuestions',
                'What should I do if I am using a "public" computer?'
              )}
            </Question>
            <Text>
              {translate(
                'faqSecurityInformationAnswers',
                'If you use a computer that others also work on...'
              )}
            </Text>
          </QuestionSection>
        )
      },
      {
        id: 'secureconnection',
        question: (): ReactElement => (
          <QuestionSection>
            <Question>
              {translate(
                'faqSecurityInformationQuestions',
                'How can I tell when my connection is secure?'
              )}
            </Question>
            <Text>
              {translate(
                'faqSecurityInformationAnswers',
                'To confirm that your information is encrypted perform the following three steps:'
              )}
            </Text>
            <List type="disc">
              <li>
                {translate(
                  'faqSecurityInformationAnswers',
                  'With a Chrome browser, right-click to the left...'
                )}
              </li>
              <li>
                {translate(
                  'faqSecurityInformationAnswers',
                  `With an Internet Explorer browser, right-click, select 'Properties'.`
                )}
              </li>
              <li>
                {translate(
                  'faqSecurityInformationAnswers',
                  'With a Firefox browser, right-click, select...'
                )}
              </li>
              <li>
                {translate(
                  'faqSecurityInformationAnswers',
                  'For Macintosh users¸ Ctrl-click¸ select...'
                )}
              </li>
            </List>
          </QuestionSection>
        )
      }
    ],
    credit: [
      {
        id: 'creditbureaudisputes',
        question: (): ReactElement => (
          <QuestionSection>
            <Question>
              {translate(
                'faqCreditQuestions',
                'Credit Bureau Questions/Disputes'
              )}
            </Question>
            <Text>
              {translate(
                'faqCreditAnswers',
                'If you believe that we have reported inaccurate information about you to a customer...',
                {
                  customerCare: contactUsBrcPlcc
                }
              )}
            </Text>
            <Stack size="small" />
            <AddressWrapper>
              <Text>
                {translate('common', addressee, {
                  addressee: brcDispute2.addressee
                })}
              </Text>
              <Text>
                {translate(
                  'faqCreditAnswers',
                  'General Inquiry Credit Bureau Dispute'
                )}
              </Text>
              <Text>
                {translate('common', addressLine1, {
                  addressLine1: brcDispute2.addressLine1
                })}
              </Text>
              <Text>
                {translate('common', addressLine2, {
                  city: brcDispute2.city,
                  state: brcDispute2.state,
                  zipCode: brcDispute2.zipCode
                })}
              </Text>
            </AddressWrapper>
            <Stack size="small" />
            <Text>
              {translate(
                'faqCreditAnswers',
                'In doing so, please identify the accurate information...'
              )}
            </Text>
          </QuestionSection>
        )
      },
      {
        id: 'increasecreditonline',
        question: (): ReactElement => (
          <QuestionSection>
            <Question>
              {translate(
                'faqCreditQuestions',
                'How do I increase my available credit online?'
              )}
            </Question>
            <Text>
              {translate(
                'faqCreditAnswers',
                'You can request an increase to your available credit...'
              )}
            </Text>
          </QuestionSection>
        )
      }
    ],
    onlinemessage: [
      {
        id: 'onlinemessagecenter',
        question: (): ReactElement => (
          <QuestionSection>
            <Question>
              {translate(
                'faqOnlineMessageCenterQuestions',
                'What is the Online Message Center?'
              )}
            </Question>
            <Text>
              {translate(
                'faqOnlineMessageCenterAnswers',
                'The Online Message Center provides a secure...'
              )}
            </Text>
          </QuestionSection>
        )
      },
      {
        id: 'viewresponses',
        question: (): ReactElement => (
          <QuestionSection>
            <Question>
              {translate(
                'faqOnlineMessageCenterQuestions',
                'How can I view responses to my requests or inquiries?'
              )}
            </Question>
            <Text>
              {translate(
                'faqOnlineMessageCenterAnswers',
                'Responses to your inquiries can only be viewed...'
              )}
            </Text>
          </QuestionSection>
        )
      },
      {
        id: 'onlinemessagesecure',
        question: (): ReactElement => (
          <QuestionSection>
            <Question>
              {translate(
                'faqOnlineMessageCenterQuestions',
                'Is the information I submit through the Online Message Center secure?'
              )}
            </Question>
            <Text>
              {translate(
                'faqOnlineMessageCenterAnswers',
                'Yes. Information submitted through the Online Message...'
              )}
            </Text>
          </QuestionSection>
        )
      },
      {
        id: 'questiontypes',
        question: (): ReactElement => (
          <QuestionSection>
            <Question>
              {translate(
                'faqOnlineMessageCenterQuestions',
                'What type of questions can I submit through the Online Message Center?'
              )}
            </Question>
            <Text>
              {translate(
                'faqOnlineMessageCenterAnswers',
                'The Online Message Center should be used for questions concerning:'
              )}
            </Text>
            <Stack size="small" />
            <List type="disc">
              <li>
                {translate(
                  'faqOnlineMessageCenterAnswers',
                  'Account Information'
                )}
                <p>
                  {translate(
                    'faqOnlineMessageCenterAnswers',
                    'Available Credit, Receipt of Payment'
                  )}
                </p>
              </li>
              <li>
                {translate(
                  'faqOnlineMessageCenterAnswers',
                  'Account Maintenance'
                )}
                <p>
                  {translate(
                    'faqOnlineMessageCenterAnswers',
                    'Current Balance, Credit Line Increase, Lost/Stolen Card'
                  )}
                </p>
              </li>
              <li>
                {translate(
                  'faqOnlineMessageCenterAnswers',
                  'Billing Inquiries'
                )}
                <p>
                  {translate(
                    'faqOnlineMessageCenterAnswers',
                    'Billing Date, Payments, Late Fees, Credit Balance Inquiry'
                  )}
                </p>
              </li>
              <li>
                {translate('faqOnlineMessageCenterAnswers', 'Disputes')}
                <p>
                  {translate(
                    'faqOnlineMessageCenterAnswers',
                    'Purchase Dispute'
                  )}
                </p>
              </li>
              <li>
                {translate(
                  'faqOnlineMessageCenterAnswers',
                  'Technical Support, eStatements, Payments, Password/Login Issues'
                )}
              </li>
              <li>{translate('faqOnlineMessageCenterAnswers', 'Other')}</li>
            </List>
          </QuestionSection>
        )
      }
    ]
  }

  return FaqQuestions
}

// returns the Accordion with the Frequently Asked Questions for P66 Plcc
const P66FAQ = ({ i18n, hasPaymentAccess, hasCreditAccess }: SectionProps) => {
  const translateQuestions = useCallback(
    (module: string, key: string, data: AnyObject = {}) =>
      i18n({ [module]: key }, { ...data }, translations),
    [i18n]
  )

  const translateModules = useCallback(
    (module: string, key: string) => i18n({ [module]: key }, {}, translations),
    [i18n]
  )

  // returns the accordion items
  const questions = getSections(translateModules, {
    hasPaymentAccess,
    hasCreditAccess
  }).map(({ sectionId, title }) => {
    const faqQuestions = getFaqQuestions(translateQuestions)
    return {
      itemId: sectionId,
      header: title,
      content: (
        <div data-test="question-section">
          {faqQuestions[sectionId].map(component => (
            <div key={component.id}>{component.question()}</div>
          ))}
        </div>
      )
    }
  })

  return <Accordion accordionItems={questions} />
}

export default P66FAQ
